<template>
  <el-upload
    class="image-upload"
    :accept="acceptTypes.join(',')"
    :list-type="(showUpload && !isMultiple) ? 'text' : 'picture'"
    :limit="limit"
    :class="[hasFileUpload && 'upload-success', isMultiple && 'is-multiple']"
    :action="qiniuUploadUrl"
    :data="qiniuData"
    :before-upload="handleBeforeUpload"
    :file-list="fileList"
    :on-success="handleUploadSuccess"
    :on-error="handleUploadError"
    :on-remove="handleFileRemove"
  >
    <el-button v-if="showUpload" size="small" type="primary">{{ $t('microSite.component.upload.clickToUpload') }}</el-button>
    <div v-if="showUpload" class="el-upload__tip" slot="tip">
      <div>{{ $t('microSite.component.upload.imgLimitTips', { imageMaxSize: imageMaxSize }) }}</div>
      <div v-if="otherTips">{{ otherTips }}</div>
    </div>
  </el-upload>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ACCEPT_FILE_TYPE } from '@/constant/crm'

export default {
  model: {
    prop: 'defaultImageUrl',
    event: 'change'
  },
  props: {
    defaultImageUrl: {
      type: [String, Array],
      default: null
    },
    otherTips: {
      type: [String],
      required: false
    }
  },
  data () {
    return {
      tips: null,
      qiniuUploadUrl: window.location.protocol + '//upload.qiniup.com',
      qiniuData: {
        key: null,
        token: null
      },
      imageUrl: null,
      hasFileUpload: false,
      isMultiple: false,
      limit: 1,
      fileList: [],
      imageMaxSize: 10,
      acceptTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'],
      uploadPrefix: 'microsite/image/'
    }
  },
  computed: {
    showUpload () {
      return (this.isMultiple || (!this.isMultiple && !this.hasFileUpload)) || !this.defaultImageUrl ||
      (this.defaultImageUrl && !(this.defaultImageUrl.indexOf(this.uploadPrefix) > -1))
    }
  },
  async created () {
    this.isMultiple = this.defaultImageUrl instanceof Array
    if (this.isMultiple) {
      this.limit = null
    }
    this.imageUrl = this.defaultImageUrl
    this.getFileList(this.defaultImageUrl)
    const resp = await this.getQiniuUploadToken({ is_public: true })
    this.qiniuData.token = resp.token
  },
  methods: {
    ...mapActions('common', ['getQiniuUploadToken']),
    ...mapGetters('account', ['getCurrentAccount']),
    handleBeforeUpload (file) {
      if (!ACCEPT_FILE_TYPE.includes(file.type)) {
        this.$message.error(this.$t('microSite.component.upload.imgTypeError'))
        return false
      }
      if (file.size > this.imageMaxSize * 1024 * 1024) {
        this.$message.error(this.$t('microSite.component.upload.imgSizeError', { imageMaxSize: this.imageMaxSize }))
        return false
      }
      this.qiniuData.key = this.fileKeyGenerator(file)
      return true
    },
    handleUploadSuccess (resp, file, fileList) {
      this.hasFileUpload = true
      const url = `${process.env.VUE_APP_QINIU_DOMAIN}${this.qiniuData.key}`
      if (this.isMultiple) {
        if (!this.imageUrl) {
          this.imageUrl = []
        }
        this.imageUrl.push(url)
      } else {
        this.imageUrl = url
      }
    },
    handleUploadError (resp, file, fileList) {
      this.hasFileUpload = false
      if (!this.isMultiple) {
        this.imageUrl = null
      }
      this.$message({
        message: this.$t('microSite.component.upload.uploadFailed'),
        duration: 2000,
        type: 'warning'
      })
    },
    handleFileRemove (file, fileList) {
      this.hasFileUpload = false
      if (this.isMultiple) {
        if (this.imageUrl) {
          const fileIndex = this.imageUrl.indexOf(file.url)
          if (fileIndex > -1) {
            this.imageUrl.splice(fileIndex, 1)
          }
        }
      } else {
        this.imageUrl = ''
      }
    },
    fileKeyGenerator (file) {
      return this.uploadPrefix + this.getCurrentAccount().id + '/' + new Date().valueOf()
    },
    getFileList (imgUrl) {
      const tempFileList = []
      if (imgUrl instanceof Array) {
        for (const item of imgUrl) {
          tempFileList.push({ name: '', url: item })
        }
      } else {
        if (imgUrl?.indexOf(this.uploadPrefix) > -1) {
          tempFileList.push({ name: '', url: imgUrl })
        }
      }
      this.fileList = tempFileList
      this.hasFileUpload = this.fileList.length > 0
    }
  },
  watch: {
    imageUrl (newVal) {
      if (newVal === this.defaultImageUrl) {
        return
      }
      this.$emit('change', newVal)
      this.getFileList(newVal)
    }
  }
}
</script>

<style lang="scss" scoped>
.image-upload {
  &.upload-success:not(.is-multiple) {
    ::v-deep {
      .el-upload--picture {
        display: none;
      }
    }
  }
  ::v-deep {
    .el-upload {
      text-align: left;
    }
    .el-upload__tip {
      line-height: initial;
      word-break: break-word;

      div {
        margin: 5px 0;
      }
    }
    .el-upload-list--picture {
      .el-upload-list__item-thumbnail {
        width: auto;
        padding: 5px;
        z-index: 0;
      }
    }
  }
}
</style>
